// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Contador/ContadorClientes.tsx
//  Carteira do escritório: os clientes que liberaram o acesso, cada um com a receita
//  do mês, a receita do ano contra o sublimite e o limite do Simples (com a projeção
//  até dezembro) e os meses sem documento enviado. Ordena pelo risco por padrão.
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, FileWarning, Search, TrendingUp, Users, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import { BarraSimples } from '@/features/cofre/FaturamentoPainel';
import { PendenciasSemana } from '@/features/contador/PendenciasSemana';
import {
  COR_SITUACAO_SIMPLES, ehRegimeNormal, GRAVIDADE_SIMPLES, percentual, ROTULO_SITUACAO_SIMPLES, variacao, type CarteiraCliente,
} from '@/features/cofre/faturamento';
import {
  competenciaAtual, competenciaFechada, contadorService, formatarDoc, rotuloCompetencia, ultimasCompetencias,
} from '@/features/contador/contadorService';

/** Sem receber documento há tantos dias: o cliente pode ter parado de emitir pelo sistema. */
const DIAS_ALERTA = 7;

type Filtro = 'todos' | 'simples' | 'envio';
type Ordem = 'risco' | 'nome' | 'mes' | 'ano';

const diasDesde = (iso?: string | null) =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : null;

const emRiscoNoSimples = (c: CarteiraCliente) => !ehRegimeNormal(c.crt) && c.projecao.situacao !== 'ok';
const semEnvio = (c: CarteiraCliente) => {
  const dias = diasDesde(c.ultimoRecebido);
  return c.mesesSemDocumento > 0 || dias === null || dias >= DIAS_ALERTA;
};

export default function ContadorClientes() {
  const navigate = useNavigate();
  const [competencia, setCompetencia] = useState(competenciaFechada());
  const emAndamento = competencia === competenciaAtual();
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [ordem, setOrdem] = useState<Ordem>('risco');
  const { data, isLoading } = useQuery({
    queryKey: ['contador', 'carteira', competencia],
    queryFn: () => contadorService.carteira(competencia),
  });

  const todos = useMemo(() => data?.clientes ?? [], [data]);
  const clientes = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const digitos = termo.replace(/\D/g, '');
    const lista = todos
      .filter((c) => !termo || (c.razaoSocial ?? '').toLowerCase().includes(termo) || (digitos.length > 0 && c.cnpjCpf.includes(digitos)))
      .filter((c) => filtro === 'todos' || (filtro === 'simples' ? emRiscoNoSimples(c) : semEnvio(c)));
    const nome = (c: CarteiraCliente) => c.razaoSocial ?? c.cnpjCpf;
    const risco = (c: CarteiraCliente) => (ehRegimeNormal(c.crt) ? -1 : GRAVIDADE_SIMPLES[c.projecao.situacao]);
    return [...lista].sort((a, b) =>
      ordem === 'nome' ? nome(a).localeCompare(nome(b))
        : ordem === 'mes' ? b.receitaMes - a.receitaMes
          : ordem === 'ano' ? b.projecao.receitaAno - a.projecao.receitaAno
            : risco(b) - risco(a) || b.projecao.projecaoAno - a.projecao.projecaoAno);
  }, [todos, busca, filtro, ordem]);

  const totalMes = todos.reduce((t, c) => t + c.receitaMes, 0);
  const totalAnterior = todos.reduce((t, c) => t + c.receitaMesAnterior, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Minha carteira</h1>
          <p className="text-muted-foreground mt-1">Faturamento e risco no Simples de cada cliente, a partir dos documentos fiscais</p>
        </div>
        <div className="sm:ml-auto flex flex-wrap gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8 w-full sm:w-56" placeholder="Nome ou CNPJ" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <Select value={String(competencia)} onValueChange={(v) => setCompetencia(Number(v))}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ultimasCompetencias().map((c) => (
                <SelectItem key={c} value={String(c)}>{rotuloCompetencia(c)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!isLoading && todos.length > 0 && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <Resumo icone={Users} titulo="Clientes" valor={String(todos.length)} ativo={filtro === 'todos'} onClick={() => setFiltro('todos')} />
          <Resumo icone={Wallet} titulo="Faturamento da carteira no mês" valor={formatCurrency(totalMes)}
            detalhe={emAndamento ? 'Mês em andamento'
              : variacao(totalMes, totalAnterior) !== null ? `${(variacao(totalMes, totalAnterior) ?? 0) >= 0 ? '+' : ''}${percentual(variacao(totalMes, totalAnterior) ?? 0)} sobre o mês anterior` : undefined} />
          <Resumo icone={TrendingUp} titulo="Risco no Simples" valor={String(todos.filter(emRiscoNoSimples).length)}
            alerta={todos.some(emRiscoNoSimples)} ativo={filtro === 'simples'} onClick={() => setFiltro(filtro === 'simples' ? 'todos' : 'simples')} />
          <Resumo icone={FileWarning} titulo="Sem documento recente" valor={String(todos.filter(semEnvio).length)}
            alerta={todos.some(semEnvio)} ativo={filtro === 'envio'} onClick={() => setFiltro(filtro === 'envio' ? 'todos' : 'envio')} />
        </div>
      )}

      {!isLoading && todos.length > 0 && <PendenciasSemana competenciaDaCarteira={competencia} />}

      {!isLoading && todos.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {filtro === 'todos' ? 'Todos os clientes' : filtro === 'simples' ? 'Clientes com risco no Simples' : 'Clientes sem documento recente'}
            {' '}· {clientes.length}
          </span>
          <Select value={ordem} onValueChange={(v) => setOrdem(v as Ordem)}>
            <SelectTrigger className="w-56 ml-auto"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="risco">Ordenar pelo risco no Simples</SelectItem>
              <SelectItem value="mes">Ordenar pela receita do mês</SelectItem>
              <SelectItem value="ano">Ordenar pela receita do ano</SelectItem>
              <SelectItem value="nome">Ordenar pelo nome</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : clientes.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {todos.length
              ? 'Nenhum cliente com esse filtro.'
              : 'Nenhum cliente liberou o acesso ainda. O cliente faz isso no Udisys Gestor, em Plataforma Contador → Acesso do contador.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {clientes.map((c) => <CartaoCliente key={c.contribuinteId} c={c} emAndamento={emAndamento} onAbrir={() =>
            navigate(`/contador/clientes/${c.contribuinteId}?competencia=${competencia}`)} />)}
        </div>
      )}
    </div>
  );
}

function Resumo({ icone: Icone, titulo, valor, detalhe, alerta = false, ativo = false, onClick }: {
  icone: typeof Users; titulo: string; valor: string; detalhe?: string; alerta?: boolean; ativo?: boolean; onClick?: () => void;
}) {
  return (
    <Card role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} onClick={onClick}
      onKeyDown={(e) => onClick && e.key === 'Enter' && onClick()}
      className={`${onClick ? 'cursor-pointer hover:border-primary' : ''} ${ativo && onClick ? 'border-primary' : alerta ? 'border-amber-500' : ''}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2 text-muted-foreground text-xs"><Icone className="h-3.5 w-3.5" />{titulo}</div>
        <p className="text-xl font-semibold mt-1 break-words">{valor}</p>
        {detalhe && <p className="text-xs text-muted-foreground mt-0.5">{detalhe}</p>}
      </CardContent>
    </Card>
  );
}

function CartaoCliente({ c, emAndamento, onAbrir }: { c: CarteiraCliente; emAndamento: boolean; onAbrir: () => void }) {
  const dias = diasDesde(c.ultimoRecebido);
  const parado = dias === null || dias >= DIAS_ALERTA;
  // Mês aberto contra um fechado daria sempre queda: só compara meses fechados.
  const v = emAndamento ? null : variacao(c.receitaMes, c.receitaMesAnterior);
  const normal = ehRegimeNormal(c.crt);
  return (
    <Card role="button" tabIndex={0} onClick={onAbrir} onKeyDown={(e) => e.key === 'Enter' && onAbrir()}
      className="min-w-0 cursor-pointer transition-colors hover:border-primary">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-start gap-2">
          <Building2 className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block truncate">{c.razaoSocial ?? formatarDoc(c.cnpjCpf)}</span>
            <span className="block text-xs font-normal text-muted-foreground">{formatarDoc(c.cnpjCpf)}{c.uf ? ` · ${c.uf}` : ''}</span>
          </span>
          {normal
            ? <Badge variant="outline" className="shrink-0">Regime normal</Badge>
            : <Badge variant="outline" className={`shrink-0 ${COR_SITUACAO_SIMPLES[c.projecao.situacao]}`}>{ROTULO_SITUACAO_SIMPLES[c.projecao.situacao]}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="font-semibold">{formatCurrency(c.receitaMes)}</p>
            <p className="text-xs text-muted-foreground">
              {emAndamento ? 'Receita do mês (em andamento)' : 'Receita do mês'}{v !== null &&<span className={v >= 0 ? ' text-green-700 dark:text-green-400' : ' text-red-700 dark:text-red-400'}>
                {' '}{v >= 0 ? '+' : ''}{percentual(v)}</span>}
            </p>
          </div>
          <div>
            <p className="font-semibold">{formatCurrency(c.projecao.receitaAno)}</p>
            <p className="text-xs text-muted-foreground">Receita de {c.projecao.ano}</p>
          </div>
        </div>
        {!normal && (
          <div>
            <BarraSimples p={c.projecao} compacta />
            <p className="text-xs text-muted-foreground mt-1">
              Projeção até dezembro: {formatCurrency(c.projecao.projecaoAno)} · RBT12 {formatCurrency(c.rbt12)}
            </p>
          </div>
        )}
        {c.projecao.situacao !== 'ok' && !normal && c.projecao.mensagem && (
          <p className="text-xs text-amber-700 dark:text-amber-400 line-clamp-2">{c.projecao.mensagem}</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {c.mesesSemDocumento > 0 && (
            <Badge variant="outline" className="gap-1 border-amber-500 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3" />{c.mesesSemDocumento} {c.mesesSemDocumento === 1 ? 'mês' : 'meses'} sem documento
            </Badge>
          )}
          {parado ? (
            <Badge variant="outline" className="gap-1 border-amber-500 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3" />
              {dias === null ? 'Nenhum documento recebido ainda' : `Sem documento novo há ${dias} dias`}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">
              {c.documentosMes} documento(s) no mês · último há {dias === 0 ? 'menos de 1 dia' : `${dias} dia(s)`}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
